import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { driveService } from "../services/DriveService";
import { DriveFolder } from "../services/DriveService";
import { createLogger } from "../utils/logger";

const logger = createLogger("GoogleDriveFolderPicker");

interface GoogleDriveFolderPickerProps {
  selectedFolderId?: string;
  selectedFolderName?: string;
  onFolderSelect: (folderId: string, folderName: string) => void;
  disabled?: boolean;
}

export const GoogleDriveFolderPicker: React.FC<GoogleDriveFolderPickerProps> = ({ selectedFolderId, selectedFolderName, onFolderSelect, disabled = false }) => {
  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedFolders = await driveService.getFolders(100);
      setFolders(fetchedFolders);
      logger.debug("Loaded folders:", fetchedFolders.length);
    } catch (err) {
      logger.error("Error loading folders:", err);
      setError("Failed to load folders. Please ensure Google Drive is connected.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderChange = (folderId: string) => {
    const folder = folders.find((f) => f.id === folderId);
    if (folder) {
      onFolderSelect(folder.id, folder.name);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Loading folders...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
        <Button onClick={loadFolders} size="small">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel id="folder-select-label">Select Google Drive Folder</InputLabel>
      <Select labelId="folder-select-label" id="folder-select" value={selectedFolderId || ""} label="Select Google Drive Folder" onChange={(e) => handleFolderChange(e.target.value)}>
        {folders.length === 0 ? (
          <MenuItem disabled value="">
            <Typography color="text.secondary">No folders found</Typography>
          </MenuItem>
        ) : (
          folders.map((folder) => (
            <MenuItem key={folder.id} value={folder.id}>
              {folder.name}
            </MenuItem>
          ))
        )}
      </Select>
      {selectedFolderId && selectedFolderName && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Selected: {selectedFolderName}
        </Typography>
      )}
      <Button onClick={loadFolders} size="small" sx={{ mt: 1 }}>
        Refresh Folders
      </Button>
    </FormControl>
  );
};
